import type { CardDefId, CardInstance, CellId, GameEvent, GameState, InstanceId, PlayerId } from '@cards/engine';
import { formatPublicPlaybackEvent } from './publicPlayback.js';

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord | undefined {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? value as UnknownRecord
    : undefined;
}

function string(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function records(value: unknown): UnknownRecord[] {
  return Array.isArray(value) ? value.map(record).filter((item): item is UnknownRecord => item !== undefined) : [];
}

/** Stable UI projection for M14 state while keeping wire/state naming at one seam. */
export interface AttachmentView {
  id: string;
  hostInstanceId: InstanceId;
  sourceInstanceId?: InstanceId;
  ownerId?: PlayerId;
  defId?: CardDefId;
}

export interface CaptureReservationView {
  id: string;
  sourceCellId?: CellId;
  destinationCellId?: CellId;
  targetInstanceId?: InstanceId;
  controllerId?: PlayerId;
}

export function controllerOf(instance: CardInstance): PlayerId {
  return string((instance as unknown as UnknownRecord).controllerId) ?? instance.ownerId;
}

export function attachmentViews(state: GameState): AttachmentView[] {
  const stateRecord = state as unknown as UnknownRecord;
  return records(stateRecord.attachments).flatMap((item, index) => {
    const hostInstanceId = string(item.hostInstanceId) ?? string(item.targetInstanceId);
    if (hostInstanceId === undefined) return [];
    const id = string(item.id) ?? string(item.attachmentId) ?? `attachment-${index}`;
    const sourceInstanceId = string(item.sourceInstanceId) ?? string(item.sourceCardInstanceId);
    const ownerId = string(item.ownerId) ?? string(item.controllerId);
    const defId = string(item.defId) ?? string(item.sourceDefId);
    return [{
      id,
      hostInstanceId,
      ...(sourceInstanceId !== undefined ? { sourceInstanceId } : {}),
      ...(ownerId !== undefined ? { ownerId } : {}),
      ...(defId !== undefined ? { defId } : {}),
    }];
  });
}

export function attachmentsFor(state: GameState, instanceId: InstanceId): AttachmentView[] {
  return attachmentViews(state).filter((attachment) => attachment.hostInstanceId === instanceId);
}

export function captureReservationViews(state: GameState): CaptureReservationView[] {
  const stateRecord = state as unknown as UnknownRecord;
  const source = stateRecord.captureReservations ?? stateRecord.reservations;
  return records(source).map((item, index) => {
    const id = string(item.id) ?? string(item.captureId) ?? `reservation-${index}`;
    const sourceCellId = string(item.sourceCellId) ?? string(item.cellId);
    const destinationCellId = string(item.destinationCellId);
    const targetInstanceId = string(item.targetInstanceId) ?? string(item.instanceId);
    const controllerId = string(item.newControllerId) ?? string(item.controllerId) ?? string(item.captorId);
    return {
      id,
      ...(sourceCellId !== undefined ? { sourceCellId } : {}),
      ...(destinationCellId !== undefined ? { destinationCellId } : {}),
      ...(targetInstanceId !== undefined ? { targetInstanceId } : {}),
      ...(controllerId !== undefined ? { controllerId } : {}),
    };
  });
}

export function reservationForCell(state: GameState, cellId: CellId): CaptureReservationView | undefined {
  return captureReservationViews(state).find((reservation) =>
    reservation.sourceCellId === cellId || reservation.destinationCellId === cellId,
  );
}

export type M14PlaybackKind = 'reaction' | 'virus' | 'capture' | 'attack' | 'assembly' | 'phase';

export interface M14PlaybackNotice {
  kind: M14PlaybackKind;
  text: string;
}

/** Maps M14 lifecycle events to concise public notices without duplicating reducers. */
export function m14PlaybackNotice(event: GameEvent): M14PlaybackNotice | undefined {
  switch (event.type) {
    case 'ReactionArmed':
    case 'ReactionTriggered':
    case 'AttachmentAdded':
    case 'AttachmentTriggered':
    case 'AttachmentRemoved':
    case 'CaptureApplied':
    case 'CaptureReturned':
    case 'CaptureFizzled':
    case 'ExtraAttackQueued':
    case 'ExtraAttackResolved': {
      const label = formatPublicPlaybackEvent(event);
      return { kind: label.kind as M14PlaybackKind, text: label.text };
    }
    case 'EffectCounterChanged':
    case 'CardsAbsorbed':
    case 'RandomDamageReplacementResolved': {
      const label = formatPublicPlaybackEvent(event);
      return { kind: label.kind as M14PlaybackKind, text: label.text };
    }
    case 'RandomUnitSelectionResolved': {
      const label = formatPublicPlaybackEvent(event);
      return { kind: label.kind as M14PlaybackKind, text: label.text };
    }
    case 'CardRevealed':
    case 'CardResolved':
    case 'CardFizzled': {
      if (event.kind !== 'REACTION' && event.kind !== 'VIRUS') return undefined;
      const label = formatPublicPlaybackEvent(event);
      return { kind: label.kind as M14PlaybackKind, text: label.text };
    }
    default:
      return undefined;
  }
}
